'use client';

import { useIsHydrated } from '@ps/ui/hooks/useIsHydrated';
import { blurhashToDataUrl } from '@ps/ui/utils/blurhashToDataUrl';

interface Props {
  blurhash?: string;
  className?: string;
}

/**
 * Fills a restricted-and-blanked slot with the media's blurhash preview (the
 * blurred backdrop the paywall lock sits on) — 1:1 with legacy
 * `blurhashVisibility="always"`. Decoded after hydration so the server HTML and
 * first client render match; before that (and when no blurhash exists) it's a
 * neutral box so layout stays stable.
 */
export function BlankedMedia({ blurhash, className }: Props) {
  const isHydrated = useIsHydrated();
  // Decode at a higher resolution than the 32px default: this preview fills a
  // large area in the post detail, and upscaling a tiny decode that far bands the
  // smooth gradient (Mach banding, most visible on a flat-light region). 128px
  // keeps the upscale small enough to stay smooth.
  const dataUrl =
    isHydrated && blurhash ? blurhashToDataUrl(blurhash, 128, 128) : undefined;
  return (
    <div
      className={`absolute inset-0 bg-neutral-900 ${className ?? ''}`}
      style={
        dataUrl
          ? {
              backgroundImage: `url(${dataUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined
      }
      aria-hidden
    />
  );
}
